'use client';
import { useContext, useEffect } from 'react';
import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';
import { AdAllianceLib } from 'components/lib/AdAlliance/adAllianceLib';
import { predictionMinHeight } from 'components/lib/AdSizePrediction';
import { AppContext } from 'context/AppContext';
import { useAdDebug } from 'hooks/useAdDebug';
import { AdSlotName, AdSlotType } from 'types/ads';
import type { PropsWithClassName } from 'types/react-props';
import styles from './AdSlot.module.scss';
import { getAdslotName, keepName, shouldPreserveSpace } from './AdSlotHelper';
import { DefaultAd, OutbrainAd } from './componentsToRender';

export type AdSlotProps = PropsWithClassName<{
  readonly name: AdSlotName;
  readonly position?: number;
  readonly viewport?: AdSlotType;
  readonly outbrainWidgetId?: string;
  readonly horizontalAlign?: 'left' | 'center' | 'right';
  readonly skipPrediction?: boolean;
}>;

const preserveSpaceStatic = '90px';

export const NO_AD_AD_ZONE = '_noad';

const isOutbrain = (name: string) => name === AdSlotName.Outbrain || name === AdSlotName.OutbrainMobile;

const shouldPushAd = (
  name: string,
  fullAdSlotName: string,
  displayBasedOnViewport: boolean,
  pushedAdSlots: Array<string>
) =>
  !isOutbrain(name) &&
  displayBasedOnViewport &&
  !pushedAdSlots.includes(fullAdSlotName) &&
  name !== AdSlotName.Outstream;

export const AdSlot = ({
  name,
  position = 1,
  viewport = AdSlotType.Default,
  outbrainWidgetId,
  horizontalAlign = 'center',
  skipPrediction = false,
  className,
}: AdSlotProps) => {
  const {
    pageMetaData: { adZone, noAds },
    adSizePrediction,
    pushedAdSlots,
    device,
    requestPath,
  } = useContext(AppContext);
  const pathname = usePathname();
  const isDebug = useAdDebug();
  const currentAdZone = pathname === '/' ? 'homepage' : adZone;
  const fullAdSlotName = keepName(name) ? name : `${name}_${position}`;
  const isForMobile = viewport === 'mobile';
  const isMobile2 = isForMobile && position === 2;
  const displayBasedOnViewport = device === viewport || viewport === AdSlotType.Default;
  const doPreserveSpace = shouldPreserveSpace(currentAdZone, name, isForMobile, position);
  const showAdSizePrediction = !skipPrediction && currentAdZone !== NO_AD_AD_ZONE;

  // AdAlliance relies on the existence of the following classname on the adSlot-wrapper
  const emsSelectorClassName = getAdslotName(viewport, fullAdSlotName, showAdSizePrediction, doPreserveSpace);

  useEffect(() => {
    if (noAds) {
      return;
    }

    if (name === AdSlotName.Empty) {
      return;
    }

    if (name === AdSlotName.HeaderKombi) {
      AdAllianceLib.pushAdSlot('wallpaper_1' as AdSlotName);
      AdAllianceLib.pushAdSlot('superbanner_1' as AdSlotName);
      pushedAdSlots.push('wallpaper_1');
      pushedAdSlots.push('superbanner_1');
    } else if (shouldPushAd(name, fullAdSlotName, displayBasedOnViewport, pushedAdSlots)) {
      AdAllianceLib.pushAdSlot(fullAdSlotName as AdSlotName);
      pushedAdSlots.push(fullAdSlotName);
    }
  }, [name, noAds, fullAdSlotName, displayBasedOnViewport, pushedAdSlots]);

  if (noAds || name === AdSlotName.Empty) {
    return null;
  }

  if (name === AdSlotName.Skyscraper && position === 2 && requestPath == '/') {
    return null;
  }

  let minHeight = !skipPrediction
    ? predictionMinHeight({
        adSizePrediction,
        adSlotName: fullAdSlotName,
        adZone: currentAdZone,
        isMobile: isForMobile,
      })
    : 'auto';

  if (doPreserveSpace) {
    minHeight = preserveSpaceStatic;
  }

  const classNames = clsx(
    emsSelectorClassName,
    styles[`${viewport}-viewport`],
    styles[horizontalAlign],
    styles[fullAdSlotName],
    className,
    {
      [styles.minHeightAuto]: minHeight === 'auto',
      [styles.mobile2]: isMobile2,
    }
  );

  if (isDebug && displayBasedOnViewport) {
    return (
      <div
        className={clsx(classNames, styles.placeholder, 'placeholder')}
        style={minHeight !== 'auto' ? { minHeight: `${minHeight}` } : {}}
        data-ada-container={`${fullAdSlotName}_${viewport}`}
      >
        Ad Slot: {fullAdSlotName}
        <br />
        type: {viewport}
        <br />
      </div>
    );
  }

  if (isOutbrain(name) && displayBasedOnViewport) {
    return (
      <OutbrainAd
        widgetId={outbrainWidgetId}
        className={clsx(styles.outbrainWrapper, styles[`${viewport}-viewport`], className)}
        fullAdSlotName={`${fullAdSlotName}_${viewport}`}
      />
    );
  }

  if (displayBasedOnViewport) {
    return (
      <DefaultAd
        className={classNames}
        minHeight={minHeight}
        adName={name}
        fullAdSlotName={fullAdSlotName}
        type={viewport}
        hidePlaceholder={minHeight === 'auto' && !isMobile2}
      />
    );
  }

  return null;
};
